import { useCurrencyContext } from '@apeiron/currency'
import { StarLite } from '@apeiron/star'
import {
  ApeironLinkButton,
  CARD_USD_OPTIONS,
  MayNull,
  noForwardCustomProps,
  noForwardProps,
  SkeletonBox
} from '@apeiron/temp'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { Box, styled, Typography } from '@mui/material'
import EthereumSVG from '@public/icons/icon-ethereum.svg'
import PriceText from '@src/components/share/generic/PriceText'
import StarCardExplain from '@src/components/stars/cards/StarCardExplain'
import { GAAssetType } from '@src/constants/googleAnalytics'
import useTracking from '@src/hooks/common/analytics/useTracking'
import usePriceCalculation from '@src/hooks/common/tools/usePriceCalculation'
import * as R from 'ramda'
import { FC, useCallback } from 'react'
import { useInView } from 'react-intersection-observer'

const Container = styled(SkeletonBox)`
  align-items: center;
  aspect-ratio: 0.7;
  display: flex;
  justify-content: center;
  ${props => props.theme.breakpoints.up('res1024')} {
    width: 245px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    width: 165px;
  }
`

const StyledButton = styled(ApeironLinkButton)`
  position: relative;
  width: 95%;
  aspect-ratio: 0.7;
  isolation: isolate;
`

const Background = styled(
  Box,
  noForwardCustomProps(['imageURL'])
)<BackgroundProps>`
  ${props =>
    props.imageURL ? `background-image: url(${props.imageURL});` : ''}
  background-repeat: no-repeat;
  background-size: 100%;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 2px;
`

const NameText = styled(Typography)`
  color: #fee0ae;
  margin-top: 5.5%;
  text-align: center;
  text-transform: uppercase;
  width: 90%;
  font-size: 14px;
  line-height: 14px;
`

const ApeironNameText = styled(Box)`
  font-family: var(--font-Apeiron);
  width: 90%;
  color: #453e38;
  text-align: center;
  font-size: 10px;
  line-height: 10px;
`

const EmptySpace = styled(Box)`
  flex: 1;
`

const InfoFooter = styled(Box)`
  padding-top: 5px;
  width: 94%;
  margin-bottom: 2.5%;
  background: linear-gradient(
    180deg,
    rgba(10, 10, 10, 0) 0%,
    rgba(10, 10, 10, 0.5) 20%,
    rgba(10, 10, 10, 0.8) 50%
  );
`

const DescriptionContainer = styled(Box)`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 5px 10px;
  width: 100%;
`

const GalaxyText = styled(Typography)`
  color: #ffffff;
  text-transform: uppercase;
  display: flex;
  gap: 4px;
  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 10px;
    font-weight: 300;
    line-height: 14px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 12px;
    font-weight: 300;
    line-height: 12px;
  }
`

const ConstellationText = styled(Typography)`
  color: #ffffff;
  margin-bottom: 2px;
  text-align: left;
  text-transform: uppercase;
  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 14px;
    font-weight: 300;
    line-height: 16px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 12px;
    font-weight: 300;
    line-height: 16px;
  }
`

const PriceContainer = styled(Box, noForwardProps)<PriceContainerProps>`
  background-color: #15151a;
  display: flex;
  gap: 10px;
  ${props => props.theme.breakpoints.up('res1024')} {
    height: 42px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    height: 28px;
  }
  ${props => props.__transparent && 'opacity: 0;'}
`

const EthPrice = styled(PriceText)`
  flex: 1.6;
  .Custom-Icon {
    ${props => props.theme.breakpoints.down('res1024')} {
      width: 16px;
      height: 16px;
    }
    margin-left: auto;
  }
  .Custom-Text {
    color: white;
    ${props => props.theme.breakpoints.up('res1024')} {
      font-size: 20px;
      font-weight: 400;
    }
    ${props => props.theme.breakpoints.down('res1024')} {
      font-size: 16px;
      font-weight: 400;
    }
  }
`

const FiatPrice = styled(PriceText)`
  flex: 1;
  padding-top: 3px;
  .Custom-Text {
    color: #999;
    ${props => props.theme.breakpoints.up('res1024')} {
      font-size: 14px;
      font-weight: 400;
    }
    ${props => props.theme.breakpoints.down('res1024')} {
      font-size: 12px;
      font-weight: 400;
    }
  }
`

const StarIdText = styled(Typography)`
  color: #ffffff;
  font-size: 12px;
  display: inline-flex;
  margin-right: 10px;
`

const StyledLocationIcon = styled(LocationOnIcon)`
  font-size: 12px;
`

const StarCard: FC<Props> = (props: Props) => {
  const {
    className,
    enableLink = true,
    target = '_self',
    showExplain = false,
    showId = false,
    star,
    noInfo = false,
    noFooter = false,
    onRenderURL,
    onClick
  } = props

  const { gtmEmitViewItem } = useTracking()

  const { getApeironPrice } = useCurrencyContext()

  const { ethPrice, usdPrice } = usePriceCalculation(star?.listingInfo)

  const formattedEthPrice = getApeironPrice(null, ethPrice)

  const formattedUsdPrice = getApeironPrice(null, usdPrice, CARD_USD_OPTIONS)

  const { ref: inViewRef } = useInView({
    triggerOnce: true,
    onChange: inView => {
      if (inView) {
        // GA-KGGb7lMxpqbLcZb
        gtmEmitViewItem({
          type: GAAssetType.Star,
          item: star
        })
      }
    }
  })

  const hasData = !R.isNil(star)

  const handleOnClick = useCallback(() => {
    if (hasData && onClick) {
      onClick(star)
    }
  }, [hasData, onClick, star])

  const url = hasData && !R.isNil(onRenderURL) ? onRenderURL(star) : null

  const tooltipId = `star-card-${star?.id || '0'}`

  return (
    <Container
      className={className}
      data-tooltip-id={tooltipId}
      loading={!hasData}
    >
      <Box ref={inViewRef} />
      {hasData ? (
        <>
          <StyledButton
            disabled={star.disabled}
            enableLink={enableLink}
            target={target}
            url={url}
            onClick={handleOnClick}
          >
            <Background imageURL={star.image}>
              {!noInfo && (
                <>
                  <NameText>
                    {showId && <StarIdText as='span'>#{star.id}</StarIdText>}
                    {` T${star.tier.level + 1} ${star.name}`}
                  </NameText>
                  <ApeironNameText>{star.name}</ApeironNameText>
                  <EmptySpace />
                  {!noFooter && (
                    <InfoFooter>
                      <DescriptionContainer>
                        <GalaxyText>
                          <StyledLocationIcon />
                          {star.galaxy?.name}
                        </GalaxyText>
                        <ConstellationText>
                          {star.constellation?.name}
                        </ConstellationText>
                      </DescriptionContainer>
                      <PriceContainer __transparent={!star.sale}>
                        {star.sale && (
                          <>
                            <EthPrice
                              icon={EthereumSVG}
                              displayPrice={formattedEthPrice}
                            />
                            <FiatPrice
                              displayPrice={formattedUsdPrice}
                              prefix='~$ '
                            />
                          </>
                        )}
                      </PriceContainer>
                    </InfoFooter>
                  )}
                </>
              )}
            </Background>
          </StyledButton>
          {showExplain && (
            <StarCardExplain starId={star.id} tooltipId={tooltipId} />
          )}
        </>
      ) : (
        <></>
      )}
    </Container>
  )
}

type BackgroundProps = {
  imageURL?: string
}

type PriceContainerProps = {
  __transparent?: boolean
}

type Props = {
  className?: string
  enableLink?: boolean
  target?: '_self' | '_blank' | '_parent' | '_top'
  noInfo?: boolean
  noFooter?: boolean
  showExplain?: boolean
  showId?: boolean
  star: MayNull<StarLite>
  onClick?: (star: StarLite) => void
  onRenderURL?: (star: StarLite) => string
}

export default StarCard
